import { useState } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import {
  BarChart2,
  FileText,
  Activity,
  Users,
  Download,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  GitPullRequest
} from 'lucide-react';

// ─── helpers ────────────────────────────────────────────────────────────────

const STATUS_LABELS = {
  Draft: 'Draft',
  Pending_EAC_Review: 'EAC Review',
  Pending_SDO_Review: 'SDO Review',
  Pending_ADC_Review: 'ADC Review',
  Pending_DC_Approval: 'DC Approval',
  Active: 'Active',
  Cancelled: 'Cancelled',
  Transferred: 'Transferred',
  Renewal_Needed: 'Renewal Needed',
  Returned: 'Returned'
};

const STATUS_COLORS = {
  Active: 'bg-green-100 text-green-800',
  Cancelled: 'bg-red-100 text-red-800',
  Draft: 'bg-gray-100 text-gray-700',
  Returned: 'bg-orange-100 text-orange-800',
  Transferred: 'bg-purple-100 text-purple-800',
  Renewal_Needed: 'bg-yellow-100 text-yellow-800',
  Pending_EAC_Review: 'bg-blue-100 text-blue-700',
  Pending_SDO_Review: 'bg-blue-100 text-blue-700',
  Pending_ADC_Review: 'bg-indigo-100 text-indigo-700',
  Pending_DC_Approval: 'bg-indigo-100 text-indigo-700'
};

const ROLE_LABELS = { bo: 'Branch Officer', eac: 'EAC', sdo: 'SDO', adc: 'ADC', dc: 'DC' };

function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function StatCard({ icon: Icon, label, value, sub, color = 'text-primary-600' }) {
  return (
    <div className="bg-white border border-secondary-200 rounded-lg p-5 flex items-start gap-4">
      <div className={`p-2 rounded-lg bg-secondary-50 ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-secondary-900">{value ?? '—'}</p>
        <p className="text-sm font-medium text-secondary-700">{label}</p>
        {sub && <p className="text-xs text-secondary-500 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

const PAGE_SIZE = 20;

// ─── Tab: Overview ──────────────────────────────────────────────────────────

function OverviewTab() {
  const { data, isLoading, error } = useQuery(
    'reports-dashboard',
    () => api.get('/reports/dashboard').then(r => r.data.dashboard),
    { refetchOnWindowFocus: false }
  );

  if (isLoading) return <Spinner />;
  if (error) return <ErrorMsg />;

  const byStatus = data.permitsByStatus || {};
  const pending = ['Pending_EAC_Review', 'Pending_SDO_Review', 'Pending_ADC_Review', 'Pending_DC_Approval']
    .reduce((s, k) => s + (byStatus[k] || 0), 0);
  const pt = data.processingTimeStats || {};

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={FileText} label="Total Permits" value={data.totalPermits} color="text-secondary-600" />
        <StatCard icon={CheckCircle} label="Active Permits" value={byStatus.Active || 0} color="text-green-600" />
        <StatCard icon={AlertCircle} label="Pending Review" value={pending} color="text-amber-600" />
        <StatCard icon={XCircle} label="Cancelled" value={byStatus.Cancelled || 0} color="text-red-500" />
      </div>

      {/* Processing time */}
      {pt.avg_processing_days && (
        <div className="bg-white border border-secondary-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-secondary-700 mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4" /> Processing Time (Submission → Approval)
          </h3>
          <div className="grid grid-cols-3 gap-6 text-center">
            <div>
              <p className="text-2xl font-bold text-secondary-900">{pt.avg_processing_days ?? '—'}</p>
              <p className="text-xs text-secondary-500 mt-1">Avg Days</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-secondary-900">{pt.min_processing_days ?? '—'}</p>
              <p className="text-xs text-secondary-500 mt-1">Fastest (Days)</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-secondary-900">{pt.max_processing_days ?? '—'}</p>
              <p className="text-xs text-secondary-500 mt-1">Slowest (Days)</p>
            </div>
          </div>
        </div>
      )}

      {/* Status breakdown */}
      <div className="bg-white border border-secondary-200 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-secondary-700 mb-3">Permits by Status</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-secondary-100">
              <th className="py-2 text-left font-medium text-secondary-600">Status</th>
              <th className="py-2 text-right font-medium text-secondary-600">Count</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-secondary-50">
            {Object.entries(byStatus).sort((a, b) => b[1] - a[1]).map(([status, count]) => (
              <tr key={status}>
                <td className="py-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-700'}`}>
                    {STATUS_LABELS[status] || status}
                  </span>
                </td>
                <td className="py-2 text-right font-semibold text-secondary-900">{count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Monthly trend */}
      {data.monthlyStats?.length > 0 && (
        <div className="bg-white border border-secondary-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-secondary-700 mb-3">Monthly Trend (Last 12 Months)</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-secondary-100">
                  <th className="py-2 text-left font-medium text-secondary-600">Month</th>
                  <th className="py-2 text-right font-medium text-secondary-600">Submitted</th>
                  <th className="py-2 text-right font-medium text-secondary-600">Approved</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-50">
                {data.monthlyStats.map((row, i) => (
                  <tr key={i}>
                    <td className="py-2 text-secondary-700">{row.month}</td>
                    <td className="py-2 text-right text-secondary-900 font-medium">{row.permits_created}</td>
                    <td className="py-2 text-right text-green-700 font-medium">{row.permits_approved}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Permit Register ───────────────────────────────────────────────────

function PermitRegisterTab() {
  const [filters, setFilters] = useState({ startDate: '', endDate: '', status: '', search: '' });
  const [applied, setApplied] = useState({});
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const buildParams = (f) => {
    const p = new URLSearchParams();
    if (f.startDate) p.set('startDate', f.startDate);
    if (f.endDate) p.set('endDate', f.endDate);
    if (f.status) p.set('status', f.status);
    if (f.search) p.set('search', f.search);
    return p.toString();
  };

  const { data, isLoading, error } = useQuery(
    ['reports-permits', applied],
    () => api.get(`/reports/permits?${buildParams(applied)}`).then(r => r.data),
    { refetchOnWindowFocus: false, keepPreviousData: true }
  );

  const permits = data?.permits || [];
  const totalPages = Math.ceil(permits.length / PAGE_SIZE);
  const paginated = permits.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleApply = () => { setApplied(filters); setPage(1); };
  const handleReset = () => { setFilters({ startDate: '', endDate: '', status: '', search: '' }); setApplied({}); setPage(1); };

  const handleExport = async () => {
    try {
      setExporting(true);
      const res = await api.get(`/reports/permits?${buildParams(applied)}&format=excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `permit_register_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white border border-secondary-200 rounded-lg p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-secondary-600 mb-1">From Date</label>
            <input type="date" value={filters.startDate}
              onChange={e => setFilters({ ...filters, startDate: e.target.value })}
              className="w-full px-3 py-1.5 border border-secondary-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-secondary-600 mb-1">To Date</label>
            <input type="date" value={filters.endDate}
              onChange={e => setFilters({ ...filters, endDate: e.target.value })}
              className="w-full px-3 py-1.5 border border-secondary-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-secondary-600 mb-1">Status</label>
            <select value={filters.status}
              onChange={e => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-3 py-1.5 border border-secondary-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
              <option value="">All Statuses</option>
              {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-secondary-600 mb-1">Search (name / village / permit no)</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-secondary-400" />
              <input type="text" value={filters.search} placeholder="Search…"
                onChange={e => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={e => e.key === 'Enter' && handleApply()}
                className="w-full pl-8 pr-3 py-1.5 border border-secondary-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button onClick={handleApply}
            className="flex items-center gap-1 px-3 py-1.5 bg-primary-600 text-white text-sm rounded hover:bg-primary-700">
            <Filter className="h-3.5 w-3.5" /> Apply
          </button>
          <button onClick={handleReset}
            className="flex items-center gap-1 px-3 py-1.5 border border-secondary-300 text-secondary-600 text-sm rounded hover:bg-secondary-50">
            <RefreshCw className="h-3.5 w-3.5" /> Reset
          </button>
          <div className="flex-1" />
          <button onClick={handleExport} disabled={exporting || permits.length === 0}
            className="flex items-center gap-1 px-3 py-1.5 border border-green-600 text-green-700 text-sm rounded hover:bg-green-50 disabled:opacity-40">
            <Download className="h-3.5 w-3.5" />
            {exporting ? 'Exporting…' : 'Export Excel'}
          </button>
        </div>
      </div>

      {/* Summary badges */}
      {data && (
        <p className="text-sm text-secondary-500">
          Showing <span className="font-medium text-secondary-700">{permits.length}</span> permits
          {data.summary?.statusBreakdown && Object.entries(data.summary.statusBreakdown).map(([s, c]) => (
            <span key={s} className={`ml-2 px-1.5 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[s] || 'bg-gray-100 text-gray-700'}`}>
              {STATUS_LABELS[s] || s}: {c}
            </span>
          ))}
        </p>
      )}

      {/* Table */}
      {isLoading ? <Spinner /> : error ? <ErrorMsg /> : permits.length === 0 ? (
        <div className="text-center py-12 bg-white border border-secondary-200 rounded-lg">
          <FileText className="h-10 w-10 text-secondary-300 mx-auto mb-2" />
          <p className="text-secondary-500 text-sm">No permits found for the selected filters.</p>
        </div>
      ) : (
        <>
          <div className="bg-white border border-secondary-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary-50 border-b border-secondary-200">
                  <tr>
                    {['#', 'Permit No', 'Applicant', 'Village', 'Subdivision', 'Status', 'Issue Date', 'Submitted By', 'Date Submitted'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-secondary-600 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-100">
                  {paginated.map((p, idx) => (
                    <tr key={p.id} className="hover:bg-secondary-50">
                      <td className="px-4 py-3 text-secondary-500 text-xs">{(page - 1) * PAGE_SIZE + idx + 1}</td>
                      <td className="px-4 py-3 font-mono text-xs text-secondary-800 whitespace-nowrap">{p.permit_no || '—'}</td>
                      <td className="px-4 py-3 font-medium text-secondary-900 whitespace-nowrap">{p.name}</td>
                      <td className="px-4 py-3 text-secondary-700">{p.village || '—'}</td>
                      <td className="px-4 py-3 text-secondary-700">{p.subdivision?.name || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-700'}`}>
                          {STATUS_LABELS[p.status] || p.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-secondary-700 whitespace-nowrap">{p.issue_date ? fmt(p.issue_date) : '—'}</td>
                      <td className="px-4 py-3 text-secondary-700 whitespace-nowrap">{p.submitter?.fullName || '—'}</td>
                      <td className="px-4 py-3 text-secondary-700 whitespace-nowrap">{fmt(p.submittedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-secondary-500">
                Page {page} of {totalPages} ({permits.length} total)
              </p>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm border border-secondary-300 rounded text-secondary-700 hover:bg-secondary-50 disabled:opacity-40">
                  <ChevronLeft className="h-4 w-4" /> Prev
                </button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm border border-secondary-300 rounded text-secondary-700 hover:bg-secondary-50 disabled:opacity-40">
                  Next <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Tab: Performance (DC only) ─────────────────────────────────────────────

function PerformanceTab() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [applied, setApplied] = useState({});

  const { data, isLoading, error } = useQuery(
    ['reports-performance', applied],
    () => {
      const p = new URLSearchParams();
      if (applied.startDate) p.set('startDate', applied.startDate);
      if (applied.endDate) p.set('endDate', applied.endDate);
      return api.get(`/reports/workflow-performance?${p}`).then(r => r.data.performance);
    },
    { refetchOnWindowFocus: false }
  );

  if (isLoading) return <Spinner />;
  if (error) return <ErrorMsg />;

  const stats = data?.overallStats || {};
  const total = parseInt(stats.total_permits) || 0;

  return (
    <div className="space-y-6">
      {/* Date filters */}
      <div className="bg-white border border-secondary-200 rounded-lg p-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-secondary-600 mb-1">From Date</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
            className="px-3 py-1.5 border border-secondary-300 rounded text-sm focus:ring-2 focus:ring-primary-500" />
        </div>
        <div>
          <label className="block text-xs font-medium text-secondary-600 mb-1">To Date</label>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
            className="px-3 py-1.5 border border-secondary-300 rounded text-sm focus:ring-2 focus:ring-primary-500" />
        </div>
        <button onClick={() => setApplied({ startDate, endDate })}
          className="px-3 py-1.5 bg-primary-600 text-white text-sm rounded hover:bg-primary-700 flex items-center gap-1">
          <Filter className="h-3.5 w-3.5" /> Apply
        </button>
      </div>

      {/* Overall stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={FileText} label="Total Permits" value={total} color="text-secondary-600" />
        <StatCard icon={CheckCircle} label="Approved" value={stats.approved_permits || 0}
          sub={total ? `${data.efficiency.approvalRate}% approval rate` : undefined} color="text-green-600" />
        <StatCard icon={RefreshCw} label="Returned" value={stats.returned_permits || 0}
          sub={total ? `${data.efficiency.returnedRate}% return rate` : undefined} color="text-orange-500" />
        <StatCard icon={Clock} label="Avg Approval Time" value={stats.avg_approval_days ? `${stats.avg_approval_days}d` : '—'}
          sub="Submission to approval" color="text-blue-600" />
      </div>

      {/* Bottlenecks */}
      {data?.bottlenecks?.length > 0 && (
        <div className="bg-white border border-secondary-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-secondary-700 mb-3 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-amber-500" /> Current Bottlenecks
          </h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-secondary-100">
                <th className="py-2 text-left font-medium text-secondary-600">Stage</th>
                <th className="py-2 text-right font-medium text-secondary-600">Pending</th>
                <th className="py-2 text-right font-medium text-secondary-600">Avg Wait (Days)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-50">
              {data.bottlenecks.map((b, i) => (
                <tr key={i}>
                  <td className="py-2 text-secondary-800">{b.current_step}</td>
                  <td className="py-2 text-right font-semibold text-amber-700">{b.pending_count}</td>
                  <td className="py-2 text-right text-secondary-700">{b.avg_pending_days ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Officer performance */}
      {data?.officerPerformance?.length > 0 && (
        <div className="bg-white border border-secondary-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-secondary-700 mb-3 flex items-center gap-2">
            <Users className="h-4 w-4" /> Officer Performance
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-secondary-100">
                  <th className="py-2 text-left font-medium text-secondary-600">Officer</th>
                  <th className="py-2 text-left font-medium text-secondary-600">Role</th>
                  <th className="py-2 text-right font-medium text-secondary-600">Reviews</th>
                  <th className="py-2 text-right font-medium text-secondary-600">Approvals</th>
                  <th className="py-2 text-right font-medium text-secondary-600">Avg Days</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-50">
                {data.officerPerformance.map((o, i) => (
                  <tr key={i}>
                    <td className="py-2 font-medium text-secondary-900">{o.full_name}</td>
                    <td className="py-2">
                      <span className="px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded text-xs font-medium">
                        {ROLE_LABELS[o.role] || o.role}
                      </span>
                    </td>
                    <td className="py-2 text-right text-secondary-800">{o.reviews_completed}</td>
                    <td className="py-2 text-right text-green-700 font-medium">{o.approvals_completed}</td>
                    <td className="py-2 text-right text-secondary-600">{o.avg_processing_days ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Activity Log ───────────────────────────────────────────────────────

function ActivityTab() {
  const [filters, setFilters] = useState({ startDate: '', endDate: '', action: '' });
  const [applied, setApplied] = useState({});

  const { data, isLoading, error } = useQuery(
    ['reports-activity', applied],
    () => {
      const p = new URLSearchParams();
      if (applied.startDate) p.set('startDate', applied.startDate);
      if (applied.endDate) p.set('endDate', applied.endDate);
      if (applied.action) p.set('action', applied.action);
      return api.get(`/reports/activity?${p}`).then(r => r.data);
    },
    { refetchOnWindowFocus: false, keepPreviousData: true }
  );

  const actions = ['created', 'updated', 'submitted', 'reviewed', 'approved', 'rejected', 'returned'];

  const ACTION_COLORS = {
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
    submitted: 'bg-blue-100 text-blue-700',
    reviewed: 'bg-indigo-100 text-indigo-700',
    returned: 'bg-orange-100 text-orange-700',
    created: 'bg-gray-100 text-gray-700',
    updated: 'bg-secondary-100 text-secondary-700'
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white border border-secondary-200 rounded-lg p-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-secondary-600 mb-1">From Date</label>
          <input type="date" value={filters.startDate} onChange={e => setFilters({ ...filters, startDate: e.target.value })}
            className="px-3 py-1.5 border border-secondary-300 rounded text-sm focus:ring-2 focus:ring-primary-500" />
        </div>
        <div>
          <label className="block text-xs font-medium text-secondary-600 mb-1">To Date</label>
          <input type="date" value={filters.endDate} onChange={e => setFilters({ ...filters, endDate: e.target.value })}
            className="px-3 py-1.5 border border-secondary-300 rounded text-sm focus:ring-2 focus:ring-primary-500" />
        </div>
        <div>
          <label className="block text-xs font-medium text-secondary-600 mb-1">Action</label>
          <select value={filters.action} onChange={e => setFilters({ ...filters, action: e.target.value })}
            className="px-3 py-1.5 border border-secondary-300 rounded text-sm focus:ring-2 focus:ring-primary-500">
            <option value="">All Actions</option>
            {actions.map(a => <option key={a} value={a}>{a.charAt(0).toUpperCase() + a.slice(1)}</option>)}
          </select>
        </div>
        <button onClick={() => setApplied(filters)}
          className="px-3 py-1.5 bg-primary-600 text-white text-sm rounded hover:bg-primary-700 flex items-center gap-1">
          <Filter className="h-3.5 w-3.5" /> Apply
        </button>
        <button onClick={() => { setFilters({ startDate: '', endDate: '', action: '' }); setApplied({}); }}
          className="px-3 py-1.5 border border-secondary-300 text-secondary-600 text-sm rounded hover:bg-secondary-50 flex items-center gap-1">
          <RefreshCw className="h-3.5 w-3.5" /> Reset
        </button>
      </div>

      {isLoading ? <Spinner /> : error ? <ErrorMsg /> : (
        <>
          {data?.activities?.length > 0 && (
            <p className="text-sm text-secondary-500">
              Showing <span className="font-medium text-secondary-700">{data.activities.length}</span> records
              {data.summary?.activityBreakdown && Object.entries(data.summary.activityBreakdown).map(([a, c]) => (
                <span key={a} className={`ml-2 px-1.5 py-0.5 rounded text-xs font-medium ${ACTION_COLORS[a] || ''}`}>
                  {a}: {c}
                </span>
              ))}
            </p>
          )}
          {data?.activities?.length === 0 ? (
            <div className="text-center py-12 bg-white border border-secondary-200 rounded-lg">
              <Activity className="h-10 w-10 text-secondary-300 mx-auto mb-2" />
              <p className="text-secondary-500 text-sm">No activity found for the selected filters.</p>
            </div>
          ) : (
            <div className="bg-white border border-secondary-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary-50 border-b border-secondary-200">
                    <tr>
                      {['Date & Time', 'Officer', 'Role', 'Action', 'Permit No', 'Permit Holder'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-secondary-600 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-100">
                    {data.activities.map((a, i) => (
                      <tr key={i} className="hover:bg-secondary-50">
                        <td className="px-4 py-3 text-secondary-600 whitespace-nowrap text-xs">
                          {new Date(a.created_at || a.createdAt).toLocaleString('en-IN')}
                        </td>
                        <td className="px-4 py-3 font-medium text-secondary-900 whitespace-nowrap">
                          {a.performer?.fullName || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-1.5 py-0.5 bg-secondary-100 text-secondary-600 rounded text-xs">
                            {ROLE_LABELS[a.performer?.role] || a.performer?.role || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${ACTION_COLORS[a.action] || 'bg-gray-100 text-gray-700'}`}>
                            {a.action}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-secondary-700">{a.Permit?.permit_no || '—'}</td>
                        <td className="px-4 py-3 text-secondary-700">{a.Permit?.name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Tab: Requests Summary ───────────────────────────────────────────────────

const REQ_STATUS_LABELS = {
  Pending_EAC_Review: 'EAC Review',
  Pending_SDO_Review: 'SDO Review',
  Pending_ADC_Review: 'ADC Review',
  Pending_DC_Approval: 'DC Approval',
  Approved: 'Approved',
  Rejected: 'Rejected',
  Cancelled: 'Cancelled'
};

const REQ_STATUS_COLORS = {
  Approved: 'bg-green-100 text-green-700',
  Rejected: 'bg-red-100 text-red-700',
  Cancelled: 'bg-gray-100 text-gray-600',
  Pending_EAC_Review: 'bg-blue-100 text-blue-700',
  Pending_SDO_Review: 'bg-blue-100 text-blue-700',
  Pending_ADC_Review: 'bg-indigo-100 text-indigo-700',
  Pending_DC_Approval: 'bg-indigo-100 text-indigo-700'
};

function RequestsSummaryTab() {
  const { data, isLoading, error } = useQuery(
    'reports-requests-summary',
    () => api.get('/reports/requests-summary').then(r => r.data),
    { refetchOnWindowFocus: false }
  );

  if (isLoading) return <Spinner />;
  if (error) return <ErrorMsg />;

  const edit = data?.editRequests || {};
  const cancel = data?.cancellationRequests || {};

  const RequestSection = ({ title, summary, icon: Icon, accentColor }) => (
    <div className="bg-white border border-secondary-200 rounded-lg p-5">
      <h3 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${accentColor}`}>
        <Icon className="h-4 w-4" /> {title}
      </h3>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        <div className="text-center p-3 bg-secondary-50 rounded-lg">
          <p className="text-xl font-bold text-secondary-900">{summary.total || 0}</p>
          <p className="text-xs text-secondary-500 mt-0.5">Total</p>
        </div>
        <div className="text-center p-3 bg-amber-50 rounded-lg">
          <p className="text-xl font-bold text-amber-700">{summary.pending || 0}</p>
          <p className="text-xs text-secondary-500 mt-0.5">Pending</p>
        </div>
        <div className="text-center p-3 bg-blue-50 rounded-lg">
          <p className="text-xl font-bold text-blue-700">{summary.recentCount || 0}</p>
          <p className="text-xs text-secondary-500 mt-0.5">Last 30 Days</p>
        </div>
      </div>

      {/* Status breakdown */}
      {Object.keys(summary.byStatus || {}).length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-secondary-100">
              <th className="py-2 text-left font-medium text-secondary-600">Status</th>
              <th className="py-2 text-right font-medium text-secondary-600">Count</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-secondary-50">
            {Object.entries(summary.byStatus)
              .sort((a, b) => b[1] - a[1])
              .map(([status, count]) => (
                <tr key={status}>
                  <td className="py-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${REQ_STATUS_COLORS[status] || 'bg-gray-100 text-gray-700'}`}>
                      {REQ_STATUS_LABELS[status] || status}
                    </span>
                  </td>
                  <td className="py-2 text-right font-semibold text-secondary-900">{count}</td>
                </tr>
              ))}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <RequestSection
        title="Edit Requests"
        summary={edit}
        icon={GitPullRequest}
        accentColor="text-blue-700"
      />
      <RequestSection
        title="Cancellation Requests"
        summary={cancel}
        icon={XCircle}
        accentColor="text-red-600"
      />
    </div>
  );
}

// ─── Shared components ───────────────────────────────────────────────────────

function Spinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
    </div>
  );
}

function ErrorMsg() {
  return (
    <div className="text-center py-12 bg-white border border-secondary-200 rounded-lg">
      <p className="text-red-500 text-sm">Failed to load report. Please try again.</p>
    </div>
  );
}

// ─── Main Reports Page ───────────────────────────────────────────────────────

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart2 },
  { id: 'permits', label: 'Permit Register', icon: FileText },
  { id: 'requests', label: 'Requests Summary', icon: GitPullRequest },
  { id: 'performance', label: 'Performance', icon: Users, dcOnly: true },
  { id: 'activity', label: 'Activity Log', icon: Activity }
];

const Reports = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');

  const visibleTabs = TABS.filter(t => !t.dcOnly || user?.role === 'dc');

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-secondary-900">Reports &amp; Analytics</h1>
        <p className="text-sm text-secondary-500 mt-1">Permit statistics, registers, and officer activity</p>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-secondary-200 mb-6 overflow-x-auto">
        {visibleTabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
              activeTab === id
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-secondary-500 hover:text-secondary-700'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && <OverviewTab />}
      {activeTab === 'permits' && <PermitRegisterTab />}
      {activeTab === 'requests' && <RequestsSummaryTab />}
      {activeTab === 'performance' && user?.role === 'dc' && <PerformanceTab />}
      {activeTab === 'activity' && <ActivityTab />}
    </div>
  );
};

export default Reports;
